import type { End, Interval, RecurrenceSpec, Rule, Unit } from "../api/generated";

export type RecurrenceKind = "every" | "after";

export const UNITS: readonly Unit[] = ["Day", "Week", "Month", "Year"];

/** The recurrence editor's draft; `none` means the task does not repeat. */
export type RecurrenceDraft =
  | { kind: "none" }
  | {
      kind: RecurrenceKind;
      count: number;
      unit: Unit;
      end: EndDraft;
    };

export type EndDraft =
  | { kind: "never" }
  | { kind: "until"; date: string }
  | { kind: "count"; times: number };

export const NO_RECURRENCE: RecurrenceDraft = { kind: "none" };

export function ruleOf(draft: Exclude<RecurrenceDraft, { kind: "none" }>): Rule {
  const interval: Interval = { n: draft.count, unit: draft.unit };
  return draft.kind === "after" ? { After: interval } : { Every: interval };
}

export function endOf(draft: EndDraft): End | undefined {
  switch (draft.kind) {
    case "never":
      return undefined;
    case "until":
      return { Until: draft.date };
    case "count":
      return { Count: draft.times };
  }
}

export function specOf(draft: Exclude<RecurrenceDraft, { kind: "none" }>): RecurrenceSpec {
  return { rule: ruleOf(draft), end: endOf(draft.end) };
}

export function draftFromSeries(rule: Rule, end: End | undefined): RecurrenceDraft {
  const [kind, interval] = "Every" in rule ? (["every", rule.Every] as const) : (["after", rule.After] as const);
  return { kind, count: interval.n, unit: interval.unit, end: endDraftOf(end) };
}

function endDraftOf(end: End | undefined): EndDraft {
  if (end === undefined) return { kind: "never" };
  return "Until" in end
    ? { kind: "until", date: end.Until }
    : { kind: "count", times: end.Count };
}

export function describeInterval(interval: Interval): string {
  const unit = interval.unit.toLowerCase();
  return interval.n === 1 ? `1 ${unit}` : `${interval.n} ${unit}s`;
}

export function describeRule(rule: Rule): string {
  return "Every" in rule
    ? `Every ${describeInterval(rule.Every)}`
    : `${describeInterval(rule.After)} after completion`;
}

export function describeEnd(end: End | undefined): string | undefined {
  if (end === undefined) return undefined;
  return "Until" in end ? `until ${end.Until}` : `${end.Count} times`;
}
